// BuilderShell — replaced the panels-and-sliders worksheet with a story chapter
// router. New player → Day 1 chapter. Returning player → DailyHub. A small
// "Start over" affordance in the corner lets playtests replay the chapter.

import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useGameState, type CatchupReport } from '@biz-kid/store';
import { IndexedDBRepo } from '@biz-kid/persistence';
import { Day1 } from '../../chapters/Day1.js';
import { DailyHub } from './DailyHub.js';
import { NameGate } from './NameGate.js';

export type BuilderShellProps = {
  catchup: CatchupReport;
};

export function BuilderShell({ catchup }: BuilderShellProps) {
  const businessCount = useGameState((s) => s.businesses.length);
  // Decide ONCE, at mount, whether this is a brand-new player. Day 1's first
  // scene starts a business, which would otherwise flip this gate and tear the
  // chapter down mid-story — so we freeze the decision in a ref. New player
  // (no saved business) → play the chapter to its end; returning player → hub.
  const startedFresh = useRef(businessCount === 0);
  const [chapterDone, setChapterDone] = useState(false);
  const showChapter = startedFresh.current && !chapterDone;

  return (
    <>
      {showChapter ? <Day1 onChapterEnd={() => setChapterDone(true)} /> : <DailyHub />}

      {/* First-run name entry — renders over everything until the player has a
          name (covers brand-new players AND existing saves from before names). */}
      <NameGate />

      {/* "While you were away" — only shown briefly after a catch-up that
          rolled engine time without the player being present. */}
      {catchup.daysAdvanced > 0 && (
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          style={{
            position: 'fixed',
            top: 64,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10,
            padding: '6px 14px',
            background: 'var(--surface)',
            backdropFilter: 'blur(8px)',
            borderRadius: 999,
            fontFamily: 'inherit',
            fontSize: 12,
            fontWeight: 700,
            color: 'var(--text-2)',
            border: '1px solid var(--border)',
            boxShadow: 'var(--shadow-sm)',
          }}
        >
          While you were away — {catchup.daysAdvanced} day
          {catchup.daysAdvanced > 1 ? 's' : ''} caught up
        </motion.div>
      )}

      <ResetCorner />
    </>
  );
}

/** Reset button — clears the saved game and reloads. Confirms first; never
 *  destroys silently. Visible enough that you can actually find it. */
function ResetCorner() {
  return (
    <button
      type="button"
      onClick={async () => {
        if (!confirm('Start over from Day 1? Your current progress will be cleared.')) return;
        try {
          const repo = new IndexedDBRepo();
          await repo.clearSnapshot('builder:default');
        } catch (e) {
          console.warn('Reset failed:', e);
        }
        window.location.reload();
      }}
      style={{
        position: 'fixed',
        bottom: 18,
        right: 18,
        zIndex: 50,
        padding: '10px 16px',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 999,
        fontFamily: 'inherit',
        fontSize: 13,
        color: 'var(--text-2)',
        fontWeight: 800,
        letterSpacing: '0.02em',
        cursor: 'pointer',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      ↻ Start over
    </button>
  );
}
